import React, { useState, useEffect } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export default function CharacterCertificate() {
  const storedSessionId = localStorage.getItem("currentSessionId");
  const [selectedSession] = useState(storedSessionId || "");

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  // Certificate fields
  const [studentName, setStudentName] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [className, setClassName] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [template, setTemplate] = useState("Template1");
  const [editableContent, setEditableContent] = useState(
    "This is to certify that the above-mentioned individual has shown excellent moral character, discipline, and respectful behaviour throughout his/her time in the institution."
  );

  // Font customization
  const [headingFontFamily, setHeadingFontFamily] = useState("Times New Roman");
  const [headingFontSize, setHeadingFontSize] = useState(30);
  const [headingFontColor, setHeadingFontColor] = useState("#1e3a8a");
  const [bodyFontFamily, setBodyFontFamily] = useState("Times New Roman");
  const [bodyFontSize, setBodyFontSize] = useState(18);
  const [bodyFontColor, setBodyFontColor] = useState("#000000");
  const [textAlign, setTextAlign] = useState("center");

  const labelStyle = "block font-semibold text-black text-[18px] mb-2";
  const inputStyle =
    "w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-800 text-[14px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  // ==========================
  // Fetch Classes
  // ==========================
  useEffect(() => {
    if (!selectedSession) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${selectedSession}`)
      .then((res) => setClasses(res.data))
      .catch((err) => console.error("Error fetching classes:", err));
  }, [selectedSession]);

  // ==========================
  // Fetch Sections by Class
  // ==========================
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }
    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(
          selectedClass
        )}/getSectionsByClassName?sessionId=${selectedSession}`
      )
      .then((res) => setSections(res.data))
      .catch((err) => console.error("Error fetching sections:", err));
  }, [selectedClass, selectedSession]);

  // ==========================
  // Fetch Students by Class & Section
  // ==========================
  useEffect(() => {
    if (!selectedClass || !selectedSection) {
      setStudents([]);
      return;
    }
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
        params: {
          class: selectedClass,
          section: selectedSection,
          sessionId: selectedSession,
          status: "active",
        },
      })
      .then((res) => setStudents(res.data))
      .catch((err) => console.error("Error fetching students:", err));
  }, [selectedClass, selectedSection, selectedSession]);

  // ==========================
  // Auto-fill student info
  // ==========================
  useEffect(() => {
    if (!selectedStudent) return;
    const stu = students.find((s) => s._id === selectedStudent);
    if (stu) {
      setStudentName(`${stu.firstName} ${stu.lastName}`);
      setFatherName(stu.fatherName || "");
      setClassName(stu.class || selectedClass);
      setSectionName(stu.section || selectedSection);
    }
  }, [selectedStudent, students]);

  // ==========================
  // Download PDF
  // ==========================
  const handleDownloadPDF = async () => {
    const certificateElement = document.getElementById("certificate-preview");
    const canvas = await html2canvas(certificateElement, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight);
    pdf.save("character_certificate.pdf");
  };

  // ==========================
  // Save Certificate to Backend
  // ==========================
const handleSaveCertificate = async () => {
    if (isProcessing) return; // prevent double click
  setIsProcessing(true);
  try {
    if (!selectedStudent) {
      alert("Please select a student");
      return;
    }
    if (!selectedSession) {
      alert("Session ID not found");
      return;
    }

    // 1️⃣ Generate PDF
    const certificateElement = document.getElementById("certificate-preview");
    const canvas = await html2canvas(certificateElement, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight);

    const pdfBlob = pdf.output("blob");
    const file = new File([pdfBlob], "certificate.pdf", { type: "application/pdf" });

    // 2️⃣ Map frontend template to backend certificateType
    const templateToCertificateTypeMap = {
      Template1: "CharacterCertificate",
      Template2: "CharacterCertificate",
      Template3: "CharacterCertificate",
    };
    const certificateType = templateToCertificateTypeMap[template];

    // 3️⃣ Create FormData
    const formData = new FormData();
    formData.append("file", file);
    formData.append("studentId", selectedStudent); 
    formData.append("certificateType", certificateType); 
    formData.append("sessionId", selectedSession); 

    // 4️⃣ POST request
    const res = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/certificates/upload`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    console.log("Certificate saved:", res.data);
    alert("Certificate created successfully!");
  } catch (error) {
    console.error("Error saving certificate:", error);
    alert("Failed to create certificate. Check console for details.");
  }
   setIsProcessing(false);
};


  // ==========================
  // Templates
  // ==========================
  const Template1 = () => (
    <div className="p-10 bg-white shadow-xl w-full border-[12px] border-[#d4af37] rounded-xl">
      <div className="border-[4px] border-blue-900 p-10 rounded-xl text-center">
        <h1
          className="text-3xl font-bold"
          style={{
            fontFamily: headingFontFamily,
            fontSize: headingFontSize,
            color: headingFontColor,
            textAlign,
          }}
        >
          CHARACTER CERTIFICATE
        </h1>
        <p className="mt-4 text-lg text-gray-700">This is to certify that</p>
        <h2
          className="text-3xl font-semibold mt-3 underline decoration-blue-900"
          style={{
            fontFamily: headingFontFamily,
            fontSize: headingFontSize - 4,
            color: headingFontColor,
            textAlign,
          }}
        >
          {studentName || "Name Surname"}
        </h2>
        <p className="mt-3">S/D of {fatherName || "__________"}</p>
        <p className="mt-3">
          Studied in Class <b>{className || "__"}</b>, Section <b>{sectionName || "__"}</b>.
        </p>
        <div
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => setEditableContent(e.currentTarget.textContent)}
          className="italic mt-6 leading-relaxed"
          style={{
            fontFamily: bodyFontFamily,
            fontSize: bodyFontSize,
            color: bodyFontColor,
            textAlign,
          }}
        >
          {editableContent}
        </div>
        <p className="mt-8">Issued on: {date}</p>
        <div className="mt-10">
          <p className="border-t-2 border-gray-700 w-64 mx-auto"></p>
          <p className="mt-2 font-semibold">Authorized Signature</p>
        </div>
      </div>
    </div>
  );

  const Template2 = () => (
    <div className="relative bg-white p-12 shadow-2xl rounded-xl border-[14px] border-[#1e3a8a] overflow-hidden">
      <div className="absolute left-0 top-0 h-full w-8 bg-[#1e3a8a]" />
      <div className="absolute right-0 top-0 h-full w-8 bg-[#fbbf24]" />
      <div className="text-center relative z-10">
        <h1
          className="text-5xl font-extrabold"
          style={{
            fontFamily: headingFontFamily,
            fontSize: headingFontSize,
            color: headingFontColor,
          }}
        >
          CHARACTER CERTIFICATE
        </h1>
        <p className="mt-4 text-lg text-gray-700">This certifies that</p>
        <h2
          className="text-4xl font-bold mt-3 underline decoration-yellow-500"
          style={{
            fontFamily: headingFontFamily,
            fontSize: headingFontSize - 6,
            color: headingFontColor,
          }}
        >
          {studentName || "Name Surname"}
        </h2>
        <p className="mt-4 text-gray-700">S/D of {fatherName || "__________"}</p>
        <p className="mt-4 text-gray-700">
          Studied in Class <b>{className || "__"}</b>, Section <b>{sectionName || "__"}</b>.
        </p>
        <div
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => setEditableContent(e.currentTarget.textContent)}
          className="italic mt-6 leading-relaxed"
          style={{
            fontFamily: bodyFontFamily,
            fontSize: bodyFontSize,
            color: bodyFontColor,
            textAlign,
          }}
        >
          {editableContent}
        </div>
        <p className="mt-8 text-gray-700">Issued on: {date}</p>
        <div className="mt-12">
          <p className="border-t-2 border-gray-700 w-56 mx-auto"></p>
          <p className="mt-2 font-semibold">Authorized Signature</p>
        </div>
      </div>
    </div>
  );

  const Template3 = () => (
    <div className="relative bg-white p-10 shadow-2xl rounded-xl border-[10px] border-[#a16b3d] overflow-hidden">
      <div className="text-center relative z-10">
        <h1
          className="text-4xl font-bold uppercase"
          style={{
            fontFamily: headingFontFamily,
            fontSize: headingFontSize,
            color: headingFontColor,
          }}
        >
          CHARACTER CERTIFICATE
        </h1>
        <p className="mt-3 text-gray-700">This certifies that</p>
        <h2
          className="text-5xl font-extrabold mt-4"
          style={{
            fontFamily: headingFontFamily,
            fontSize: headingFontSize - 6,
            color: headingFontColor,
          }}
        >
          {studentName || "Name Surname"}
        </h2>
        <p className="mt-4 text-lg">S/D of {fatherName || "__________"}</p>
        <p className="mt-4 text-lg">
          Studied in Class <b>{className || "__"}</b>, Section <b>{sectionName || "__"}</b>.
        </p>
        <div
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => setEditableContent(e.currentTarget.textContent)}
          className="italic mt-6 leading-relaxed"
          style={{
            fontFamily: bodyFontFamily,
            fontSize: bodyFontSize,
            color: bodyFontColor,
            textAlign,
          }}
        >
          {editableContent}
        </div>
        <p className="mt-8">Issued on: {date}</p>
        <div className="mt-12">
          <p className="border-t-2 border-gray-700 w-48 mx-auto"></p>
          <p className="mt-2 font-semibold">Authorized Signature</p>
        </div>
      </div>
    </div>
  );

  // ==========================
  // JSX
  // ==========================
  return (
    <div className="p-6 bg-white shadow-md rounded-lg w-full mx-auto max-w-screen-xl">
      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className={labelStyle}>Class</label>
          <select
            className={inputStyle}
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">Select Class</option>
            {classes.map((cls) => (
              <option key={cls._id} value={cls.className}>
                {cls.className}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelStyle}>Section</label>
          <select
            className={inputStyle}
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
          >
            <option value="">Select Section</option>
            {sections.map((sec) => (
              <option key={sec._id} value={sec.sectionName}>
                {sec.sectionName}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelStyle}>Student</label>
          <select
            className={inputStyle}
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
          >
            <option value="">Select Student</option>
            {students.map((stu) => (
              <option key={stu._id} value={stu._id}>
                {stu.firstName} {stu.lastName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Templates + Font Customization */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className={labelStyle}>Select Template</label>
          <select
            className={inputStyle}
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          >
            <option value="Template1">Template 1</option>
            <option value="Template2">Template 2</option>
            <option value="Template3">Template 3</option>
          </select>
        </div>
        <div>
          <label className={labelStyle}>Issued Date</label>
          <input
            type="date"
            className={inputStyle}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>

      {/* Certificate Preview */}
      <div id="certificate-preview" className="mt-6">
        {template === "Template1" && <Template1 />}
        {template === "Template2" && <Template2 />}
        {template === "Template3" && <Template3 />}
      </div>

      {/* Buttons */}
      <div className="flex flex-wrap gap-4 mt-6">
        <button
          onClick={handleDownloadPDF}
          className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-lg hover:bg-green-700"
        >
          Download PDF
        </button>
        <button
          onClick={handleSaveCertificate}
          className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-lg hover:bg-blue-700"
        >
          Create Certificate
        </button>
      </div>
    </div>
  );
}
